import { Link } from "../components/LocaleLink";
import {
  Scale, CheckCircle2, ChevronRight, ArrowRight, Globe2, Shield, Users, Quote,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track } from "../lib/analytics";
import { JsonLd, buildBreadcrumbSchema, buildFaqSchema } from "../components/JsonLd";

const origin = () =>
  typeof window !== "undefined" ? window.location.origin : "https://velmiraliving.com";

const PROVIDERS = [
  {
    name: "Velmira Living",
    model: "Boutique owner-first manager",
    focus: "Short-term + long-term + concierge (hybrid)",
    langs: "EN · FR · TR · ES",
    fee: "Custom quote (full-service)",
    best: "Absentee international owners who want one accountable, on-the-ground team for everything",
    us: true,
  },
  {
    name: "GuestReady",
    model: "Global tech-driven STR operator",
    focus: "Short-term rentals (Airbnb / Booking)",
    langs: "EN · FR",
    fee: "From ≈20% of rental revenue*",
    best: "Owners who prefer a large platform with an app-first, standardised experience",
  },
  {
    name: "HostnFly",
    model: "French algorithmic STR manager",
    focus: "Short-term rentals, dynamic pricing",
    langs: "FR · EN",
    fee: "From ≈20% of rental revenue*",
    best: "Hands-off owners comfortable with a remote-first, algorithm-led model",
  },
  {
    name: "UpperKey",
    model: "Guaranteed-rent operator",
    focus: "They become your tenant and sublet",
    langs: "EN · FR",
    fee: "Fixed monthly rent paid to you",
    best: "Owners who prefer predictable income over seasonal upside",
  },
  {
    name: "Traditional agence immobilière",
    model: "Local long-term letting agency (Century 21, Orpi, independents)",
    focus: "Long-term (unfurnished/furnished) letting",
    langs: "FR (mostly)",
    fee: "≈6–10% of rent (excl. VAT) + letting fees*",
    best: "Classic multi-year leases for owners fluent in French processes",
  },
  {
    name: "Self-management",
    model: "DIY with channel managers & cleaners",
    focus: "Whatever you can handle",
    langs: "—",
    fee: "Your time + tool subscriptions",
    best: "Local owners with free time, French fluency and one easy property",
  },
];

const FAQ = [
  {
    q: "What is the best property management company in Nice for foreign owners?",
    a: "It depends on the model you want. For absentee international owners (American, Turkish, British) who want one accountable team covering short-term rentals, maintenance and concierge in their language, a boutique operator like Velmira Living is the strongest fit. For a standardised app-first experience at scale, GuestReady or HostnFly are established alternatives. For predictable fixed income with no upside, a guaranteed-rent operator like UpperKey works. For classic long-term leases, a traditional French agence immobilière remains the default.",
  },
  {
    q: "How much does property management cost in Nice in 2026?",
    a: "Short-term rental (Airbnb) management in Nice is typically priced as a percentage of rental revenue; the large operators publish rates starting around 20%, and conciergeries commonly quote 20–30% depending on service depth. Traditional long-term letting agencies typically charge around 6–10% of rent (excl. VAT) for ongoing management, plus separate letting fees — ANIL cites 6–8% as the usual range. Guaranteed-rent operators pay you a fixed monthly amount and keep the difference. Boutique full-service managers price per property based on scope — always request a written quote.",
  },
  {
    q: "Is guaranteed rent worth it for a Nice apartment?",
    a: "Guaranteed rent removes vacancy risk, but the fixed amount is generally set below what a well-run, compliant short-term rental earns in prime Nice locations (Old Town, Carré d'Or), because the operator keeps the upside in exchange for carrying the risk. It suits owners who value certainty over yield, or properties in weaker micro-locations.",
  },
  {
    q: "Can I manage my Nice property remotely from the US or Turkey?",
    a: "Legally yes, practically it is hard: mairie registration, taxe de séjour filings, LMNP declarations, French-speaking contractors and 24/7 guest issues all require local presence or a local partner. Most absentee owners use a professional manager — the cost is usually offset by higher occupancy and compliant operation.",
  },
];

const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

export default function BestPropertyManagementNice() {
  const { lang, t } = useLang();
  const url = `${origin()}/${lang}/best-property-management-nice`;
  const breadcrumb = [
    { name: t("blogUi.home"), url: `${origin()}/${lang}` },
    { name: "Best Property Management in Nice", url },
  ];

  return (
    <div data-testid="best-pm-nice-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Best Property Management Companies in Nice 2026 | Velmira Living"
        description="Honest 2026 comparison of property management in Nice: boutique managers, GuestReady, HostnFly, guaranteed rent, agencies and DIY — fees and who each suits."
      />
      <JsonLd id="bestpm-breadcrumb" data={buildBreadcrumbSchema(breadcrumb)} />
      <JsonLd id="bestpm-faq" data={buildFaqSchema(FAQ)} />
      <JsonLd
        id="bestpm-itemlist"
        data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Property management options in Nice, France (2026)",
          itemListOrder: "https://schema.org/ItemListUnordered",
          numberOfItems: PROVIDERS.length,
          itemListElement: PROVIDERS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.name,
            description: `${p.model} — ${p.focus}. Best for: ${p.best}`,
          })),
        }}
      />

      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1100px]">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-8">
          <Link to="/" className="hover:text-primary">{t("blogUi.home")}</Link>
          <ChevronRight size={12} />
          <span className="text-primary">Best Property Management Nice</span>
        </nav>

        <Reveal>
          <p className={overline}>
            <Scale size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
            Honest comparison · Updated 2026
          </p>
          <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
            Best property management companies in Nice — a 2026 comparison.
          </h1>
          <p className="mt-8 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-3xl">
            There is no single &ldquo;best&rdquo; manager — there is the best model for your
            property, language and involvement level. Below is a factual comparison of the main
            options available to owners in Nice and the French Riviera in 2026: boutique
            full-service managers, tech-driven platforms, guaranteed-rent operators, traditional
            agencies and self-management.
          </p>
        </Reveal>

        {/* DISCLOSURE */}
        <Reveal delay={0.1}>
          <div data-testid="bestpm-disclosure" className="mt-10 border-l-2 border-primary/60 bg-secondary p-6 max-w-3xl">
            <p className="font-body font-light text-sm text-foreground/80 leading-relaxed">
              <Shield size={14} className="inline text-primary mr-2 -mt-0.5" />
              <strong className="text-foreground">Transparency:</strong> this page is published by
              Velmira Living and yes, we appear in the list. Descriptions of other operators are
              kept factual and neutral. Fee figures marked * are indicative, based on rates
              published by each provider as of mid-2026 and may vary by property and city — always
              confirm current terms directly with each provider.
            </p>
          </div>
        </Reveal>

        {/* COMPARISON TABLE */}
        <Reveal>
          <section className="mt-16">
            <p className={overline}>Side by side</p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              The six ways to run a Nice property
            </h2>
            <div className="mt-8 border border-border/60 overflow-x-auto">
              <table data-testid="bestpm-table" className="w-full text-left font-body text-sm min-w-[900px]">
                <thead className="bg-secondary">
                  <tr className="font-accent text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    <th className="px-5 py-4">Provider / option</th>
                    <th className="px-5 py-4">Model</th>
                    <th className="px-5 py-4">Focus</th>
                    <th className="px-5 py-4">Languages</th>
                    <th className="px-5 py-4">Typical fee</th>
                  </tr>
                </thead>
                <tbody>
                  {PROVIDERS.map((p) => (
                    <tr
                      key={p.name}
                      className={`border-t border-border/50 transition-colors ${p.us ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-secondary/60"}`}
                    >
                      <td className="px-5 py-4 font-headings text-foreground whitespace-nowrap">
                        {p.name}{p.us ? <span className="ml-2 font-accent text-[9px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-1.5 py-0.5">that&rsquo;s us</span> : null}
                      </td>
                      <td className="px-5 py-4 text-foreground/80">{p.model}</td>
                      <td className="px-5 py-4 text-foreground/70">{p.focus}</td>
                      <td className="px-5 py-4 text-foreground/70 whitespace-nowrap">{p.langs}</td>
                      <td className="px-5 py-4 text-primary">{p.fee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </Reveal>

        {/* WHO SHOULD CHOOSE WHAT */}
        <Reveal>
          <section className="mt-20">
            <p className={overline}>
              <Users size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              Decision guide
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              Who should choose what
            </h2>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {PROVIDERS.map((p) => (
                <li key={p.name} className={`border p-6 ${p.us ? "border-primary/50 bg-secondary" : "border-border/60 bg-secondary"}`}>
                  <p className="font-headings text-xl text-foreground">{p.name}</p>
                  <p className="mt-3 font-body font-light text-sm text-foreground/75 leading-relaxed">
                    <CheckCircle2 size={14} className="inline text-primary mr-1.5 -mt-0.5" />
                    {p.best}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        {/* WHY BOUTIQUE */}
        <Reveal>
          <section className="mt-20 border-l-2 border-primary/60 pl-6 py-2 max-w-3xl">
            <p className={overline}>Our honest take</p>
            <p className="mt-4 font-body font-light text-lg text-foreground/85 leading-relaxed">
              If you live abroad and own one or two prime Riviera properties, the numbers usually
              favour a compliant, hands-on operator over both DIY and guaranteed rent: according to
              our 2026 Riviera data report, registered short-term rentals in prime Nice sustained{" "}
              <strong className="text-foreground">78%+ occupancy at €215–245 ADR</strong>, while the
              city introduced an annual authorisation quota for secondary residences in four zones.
              The real differentiator between managers is accountability — one named person per
              property, in your language, who answers when a pipe bursts at 2am. That is the model
              behind our{" "}
              <Link to="/airbnb-management-nice" className="text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary">
                Airbnb and short-term rental management in Nice
              </Link>.
            </p>
            <Link to="/riviera-real-estate-data-2026" className="mt-5 inline-flex items-center gap-2 text-primary font-accent text-xs uppercase tracking-[0.2em] hover:underline">
              See the full 2026 data report <ArrowRight size={13} />
            </Link>
          </section>
        </Reveal>

        {/* FAQ */}
        <Reveal>
          <section className="mt-20">
            <p className={overline}>
              <Quote size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              Frequently asked
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              Property management in Nice — FAQ
            </h2>
            <div className="mt-8 space-y-5">
              {FAQ.map((f) => (
                <div key={f.q} className="border border-border/60 bg-secondary p-6 md:p-7">
                  <h3 className="font-headings text-xl text-foreground leading-tight">{f.q}</h3>
                  <p className="mt-3 font-body font-light text-foreground/75 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* CTA */}
        <Reveal>
          <div data-testid="bestpm-cta" className="mt-20 border border-primary/30 bg-secondary p-10 md:p-14 text-center">
            <Globe2 size={26} className="text-primary mx-auto" strokeWidth={1.4} />
            <h2 className="mt-6 font-headings text-3xl sm:text-4xl leading-tight text-foreground">
              {t("landing.ownerCtaTitle")}
            </h2>
            <p className="mt-4 font-body font-light text-foreground/75 max-w-xl mx-auto">
              {t("landing.ownerCtaText")}
            </p>
            <Link
              to="/owner"
              onClick={() => track("property_management_cta_click", { cta_location: "best_property_management_page", lead_type: "owner" })}
              data-testid="bestpm-cta-button"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all"
            >
              {t("ownerCta.button")} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
